import styled from '@emotion/styled';
import { useField } from 'formik';
import React, { useMemo } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';

import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import ToggleSwitchField from 'Shared/form/ToggleSwitchField';
import assessmentSettingsPropType from '../../types/assessmentSettings';
import questionPropType from '../types/questionPropType';

const ToggleRowStyle = styled.div`
    display: flex;
    gap: 2em;
`;

const QuestionOptionsField = ({ question, assessment }) => {
    const isClinicalAdmin = useIsPermitted(PERMISSION_TYPES.CLINICAL_ADMIN);
    const [, , { setValue: setRequired }] = useField(`questions.${question.question_id}.attributes.required`);
    const [, , { setValue: setHidden }] = useField(`questions.${question.question_id}.attributes.hidden`);

    const options = useMemo(() => {
        const result = [];
        if (isClinicalAdmin || !question.attributes.therigy_required) {
            result.push(
                <ToggleSwitchField
                    key="required"
                    fieldName={`questions.${question.question_id}.attributes.required`}
                    label="Required"
                    onChange={(newValue) => {
                        setRequired(newValue);
                        if (newValue) {
                            setHidden(!newValue);
                        }
                    }}
                />
            );
        }
        result.push(
            <ToggleSwitchField
                key="hidden"
                fieldName={`questions.${question.question_id}.attributes.hidden`}
                label={isClinicalAdmin ? 'Disabled' : 'Hidden'}
                onChange={(newValue) => {
                    setHidden(newValue);
                    if (newValue) {
                        setRequired(!newValue);
                    }
                }}
            />
        );
        if (question.type !== 'message') {
            const isDisabled = !isClinicalAdmin && !question.mobile_question && !question.can_delete;
            const mobileField = (
                <ToggleSwitchField
                    key="is_mobile"
                    fieldName={`questions.${question.question_id}.attributes.is_mobile`}
                    label="Mobile"
                    disabled={isDisabled}
                />
            );
            const mobileFieldWithPopup = !isDisabled ? (
                mobileField
            ) : (
                <OverlayTrigger
                    key="is_mobile"
                    placement="top"
                    overlay={
                        <Popover id="is_mobile_disabled_popover">
                            <Popover.Content>Not yet configured for Mobile</Popover.Content>
                        </Popover>
                    }
                >
                    <div>{mobileField}</div>
                </OverlayTrigger>
            );
            result.push(mobileFieldWithPopup);
        }
        if (isClinicalAdmin) {
            result.push(
                <ToggleSwitchField
                    key="locked"
                    fieldName={`questions.${question.question_id}.attributes.locked`}
                    label="Lock Text"
                />
            );
        }
        if (assessment.ehrEncounterNote && question.type !== 'message') {
            result.push(
                <ToggleSwitchField
                    key="ehr_encounter_note"
                    fieldName={`questions.${question.question_id}.attributes.ehr_encounter_note`}
                    label="EHR Encounter Note"
                />
            );
        }
        return result;
    }, [
        assessment.ehrEncounterNote,
        isClinicalAdmin,
        question.attributes.therigy_required,
        question.can_delete,
        question.mobile_question,
        question.question_id,
        question.type,
        setHidden,
        setRequired,
    ]);

    if (options.length === 0) {
        return null;
    }

    return (
        <fieldset>
            <legend>Question Options</legend>
            <ToggleRowStyle>{options}</ToggleRowStyle>
        </fieldset>
    );
};

QuestionOptionsField.propTypes = {
    question: questionPropType.isRequired,
    assessment: assessmentSettingsPropType.isRequired,
};

export default QuestionOptionsField;
